import {z} from 'zod';

import {IsoUtcDateTimeSchema, UuidV4Schema} from './common.js';

/**
 * Contratos do radar de virais.
 *
 * ## O que este arquivo existe para IMPEDIR
 *
 * O pedido original era repostar mídia de terceiro. Ele foi recusado, e o dono
 * aprovou o radar no lugar. A recusa não vive num comentário: ela vive na forma
 * dos tipos daqui. **Não existe campo para bytes, para caminho de arquivo, nem
 * para identificador de objeto de mídia nosso.** O que atravessa é metadado e
 * ENDEREÇO, e a ausência do resto é a cerca.
 *
 * ## Legenda de terceiro é DADO
 *
 * `caption` entra cortada e serve a duas coisas: duas contas de palavra na
 * pontuação, e o dono ler na tela. Ela nunca vira instrução. O teste da
 * pontuação prova isso com uma legenda que carrega uma ordem escrita para um
 * modelo: a nota tem que sair idêntica com e sem a ordem dentro.
 */

// ---------------------------------------------------------------------------
// A lista curada de perfis
// ---------------------------------------------------------------------------

/**
 * Um nome de usuário do Instagram, SEM arroba.
 *
 * Sem arroba porque é assim que a Graph pede em `business_discovery.username()`
 * e é assim que a lista guarda. Aceitar as duas formas produziria duas grafias
 * do mesmo perfil e, com elas, duas linhas do mesmo post no painel.
 */
export const InstagramUsernameSchema = z
  .string()
  .trim()
  .regex(
    /^[A-Za-z0-9._]{1,60}$/u,
    'nome de usuário sem arroba, com letras, números, ponto e sublinhado',
  );

export const ReferenceProfileSchema = z.strictObject({
  usuario: InstagramUsernameSchema,
  /**
   * Por que este perfil está na lista, escrito por gente.
   *
   * Obrigatório, e é o campo que impede a lista de virar depósito. Um perfil
   * que ninguém consegue justificar por escrito não entra, e um que ninguém
   * consegue mais justificar sai.
   */
  motivo: z.string().trim().min(20).max(400),
  idioma: z.enum(['pt', 'en', 'es']),
  tema: z.string().trim().min(3).max(120),
});
export type ReferenceProfile = z.infer<typeof ReferenceProfileSchema>;

export const ReferenceProfileFileSchema = z.strictObject({
  /** JSON não tem comentário, e arquivo de política sem motivo vira lista. */
  sobre: z.string().trim().min(20).max(3_000),
  perfis: z.array(ReferenceProfileSchema).min(1).max(60),
});
export type ReferenceProfileFile = z.infer<typeof ReferenceProfileFileSchema>;

// ---------------------------------------------------------------------------
// A mídia descoberta
// ---------------------------------------------------------------------------

/**
 * Os tipos de mídia que a Graph declara.
 *
 * Lista de PERMISSÃO: um tipo novo não vira "outro" nem cai num padrão, ele
 * derruba a leitura. É a mesma doutrina de `RemoteContainerStatusSchema` no
 * cliente da Meta, e pelo mesmo motivo: um valor que este sistema não conhece
 * não pode ser tratado como um que ele conhece.
 */
export const ViralMediaTypeSchema = z.enum([
  'REELS',
  'VIDEO',
  'IMAGE',
  'CAROUSEL_ALBUM',
]);
export type ViralMediaType = z.infer<typeof ViralMediaTypeSchema>;

/** Quantos caracteres de legenda alheia atravessam. */
export const MAX_CAPTION_LENGTH = 600;

/**
 * Uma leitura de mídia, já normalizada, saindo do descobridor.
 *
 * É o que a porta de descoberta devolve, independentemente de qual
 * implementação a produziu. Ela não tem pontuação: pontuar é do domínio, e
 * misturar as duas faria a regra depender de quem leu.
 */
export const DiscoveredMediaSchema = z.strictObject({
  profileUsername: InstagramUsernameSchema,
  followerCount: z.int().positive(),
  remoteMediaId: z.string().regex(/^[0-9]{1,32}$/u),
  mediaType: ViralMediaTypeSchema,
  permalink: z.url().refine((valor) => valor.startsWith('https://'), {
    message: 'o permalink precisa ser https',
  }),
  caption: z.string().max(MAX_CAPTION_LENGTH).nullable(),
  thumbnailUrl: z
    .url()
    .max(2_000)
    .refine((valor) => valor.startsWith('https://'), {
      message: 'a miniatura precisa ser https',
    })
    .nullable(),
  postedAt: IsoUtcDateTimeSchema,
  likeCount: z.int().nonnegative(),
  commentCount: z.int().nonnegative(),
  /** Nulo é "a Graph não mandou", que não é a mesma coisa que zero. */
  viewCount: z.int().nonnegative().nullable(),
});
export type DiscoveredMedia = z.infer<typeof DiscoveredMediaSchema>;

// ---------------------------------------------------------------------------
// A leitura do gerador
// ---------------------------------------------------------------------------

/**
 * O que o gerador pode dizer sobre um viral, e SÓ isso.
 *
 * Schema fechado, campo a campo, com teto de tamanho em cada um. O gerador leu
 * legenda de terceiro para escrever isto, e legenda de terceiro pode conter
 * instrução: o schema é o que garante que a resposta caiba no formato esperado
 * em vez de virar um texto que alguém coloca na tela sem ler.
 */
export const ViralReadingSchema = z.strictObject({
  hook: z.string().trim().min(3).max(120),
  whyItWorked: z.string().trim().min(10).max(400),
  format: z.string().trim().min(3).max(80),
});
export type ViralReading = z.infer<typeof ViralReadingSchema>;

// ---------------------------------------------------------------------------
// O relatório de uma rodada
// ---------------------------------------------------------------------------

/**
 * Por que uma rodada não produziu nada.
 *
 * Lista de PERMISSÃO de motivos, e não texto livre, porque o painel e o CLI
 * traduzem cada um deles para uma frase que o dono entende, e um motivo novo
 * precisa ganhar tradução em vez de aparecer cru na tela.
 */
export const RadarUnavailableReasonSchema = z.enum([
  /**
   * O estado de 2026-09-05, e o mais importante deles.
   *
   * A conta usa a Instagram API com Instagram Login, e `business_discovery` é
   * da variante com Facebook Login. Sem a Página vinculada e o segundo token,
   * não há de onde ler mídia de terceiro. Ver a seção de documentação do plano
   * `09-radar-de-virais.md`.
   */
  'RADAR_SEM_CREDENCIAL',
  /** A lista curada existe e está vazia depois dos filtros. */
  'RADAR_SEM_PERFIS',
  /** A Graph respondeu, e respondeu recusando. O código dela vai junto. */
  'RADAR_RECUSADO_PELA_GRAPH',
  /**
   * A rodada saiu, perguntou a todos, e TODOS recusaram.
   *
   * Ela é diferente de `RADAR_SEM_CREDENCIAL` (que é "nem chegamos a
   * perguntar") e diferente de lista vazia (que seria "perguntamos e nada
   * passou da régua"). O que houve aqui é que a leitura foi tentada perfil a
   * perfil e nenhuma resposta veio, e o código da primeira recusa acompanha.
   */
  'RADAR_TODOS_RECUSADOS',
  /**
   * A lista curada de perfis NÃO está configurada neste ambiente.
   *
   * Ausência de MONTAGEM, e não ausência de acesso: sem a lista não existe a
   * quem perguntar, e nenhuma credencial nova mudaria isso.
   */
  'RADAR_NAO_CONFIGURADO',
  /**
   * A montagem existe e NENHUMA rodada foi gravada até hoje.
   *
   * É o motivo que o painel devolvia como se fosse leitura: até 06/09/2026 a
   * tela dizia "o radar rodou" só porque o caminho da lista estava preenchido.
   * Caminho configurado é configuração; rodada é recibo.
   */
  'RADAR_NUNCA_EXECUTADO',
  /**
   * A última rodada abriu e não fechou.
   *
   * Um processo derrubado no meio da leitura deixa exatamente isto, e o que ele
   * NÃO deixa é resultado. Mostrar a lista anterior como se fosse de hoje seria
   * datar errado o que está na tela.
   */
  'RADAR_RODADA_INCOMPLETA',
  /** A última rodada terminou em erro. A classe sanitizada vai junto. */
  'RADAR_RODADA_FALHOU',
]);
export type RadarUnavailableReason = z.infer<
  typeof RadarUnavailableReasonSchema
>;

export const RadarDiscoveryLineSchema = z.strictObject({
  profileUsername: InstagramUsernameSchema,
  desfecho: z.enum(['lido', 'recusado']),
  /** Quantas mídias entraram ou foram atualizadas por este perfil. */
  midias: z.int().nonnegative(),
  /** O código que a Graph devolveu, quando ela recusou. */
  codigo: z.string().max(120).nullable(),
});
export type RadarDiscoveryLineDto = z.infer<typeof RadarDiscoveryLineSchema>;

export const RadarDiscoveryReportSchema = z.discriminatedUnion('availability', [
  z.strictObject({
    availability: z.literal('available'),
    aplicou: z.boolean(),
    perfisLidos: z.int().nonnegative(),
    perfisFalhos: z.int().nonnegative(),
    midiasNovas: z.int().nonnegative(),
    midiasAtualizadas: z.int().nonnegative(),
    apagadasPorRetencao: z.int().nonnegative(),
    pautasCriadas: z.int().nonnegative(),
    linhas: z.array(RadarDiscoveryLineSchema),
  }),
  z.strictObject({
    availability: z.literal('unavailable'),
    reason: RadarUnavailableReasonSchema,
    /** O código cru da Graph, quando houve um. Nunca o token, nunca a URL. */
    graphCode: z.string().max(120).nullable(),
  }),
]);
export type RadarDiscoveryReportDto = z.infer<
  typeof RadarDiscoveryReportSchema
>;

export const RadarDiscoveryRequestSchema = z.strictObject({
  asOf: IsoUtcDateTimeSchema,
});
export type RadarDiscoveryRequest = z.infer<typeof RadarDiscoveryRequestSchema>;

// ---------------------------------------------------------------------------
// O RECIBO da rodada
// ---------------------------------------------------------------------------

/**
 * Como uma rodada terminou.
 *
 * Lista de PERMISSÃO, e os seis não são graus da mesma coisa: são fatos
 * diferentes que levam o dono a decisões diferentes.
 *
 * O par que este lote existe para separar é `vazio` e `indisponivel`. `vazio` é
 * "perguntamos a todos, todos responderam, e nada passou da régua": é notícia
 * sobre o MERCADO. `indisponivel` é "não temos por onde olhar": é notícia sobre
 * NÓS. Até 06/09/2026 os dois saíam na tela como a mesma frase.
 */
export const RadarRunOutcomeSchema = z.enum([
  'em_andamento',
  'sucesso',
  'parcial',
  'vazio',
  'indisponivel',
  'falha',
]);
export type RadarRunOutcome = z.infer<typeof RadarRunOutcomeSchema>;

/** Quem mandou a rodada acontecer. */
export const RadarRunTriggerSchema = z.enum(['cli', 'agendador']);
export type RadarRunTrigger = z.infer<typeof RadarRunTriggerSchema>;

/**
 * O recibo de uma rodada, como a tela o lê.
 *
 * Ele existe porque a pergunta "o radar rodou?" não se responde olhando para o
 * banco de mídias: banco vazio significa as duas coisas ao mesmo tempo, e
 * adivinhar por ele é o achado F14 inteiro.
 *
 * `finishedAt` nulo é um FATO, e não um campo esquecido: é a rodada que abriu e
 * não fechou, que é o que um processo derrubado no meio da leitura deixa.
 */
export const RadarRunReceiptSchema = z.strictObject({
  startedAt: IsoUtcDateTimeSchema,
  finishedAt: IsoUtcDateTimeSchema.nullable(),
  outcome: RadarRunOutcomeSchema,
  reason: RadarUnavailableReasonSchema.nullable(),
  graphCode: z.string().max(120).nullable(),
  /** Ensaio não conta como rodada aplicada, e a tela precisa saber qual foi. */
  applied: z.boolean(),
  trigger: RadarRunTriggerSchema,
  profilesRequested: z.int().nonnegative(),
  profilesRead: z.int().nonnegative(),
  profilesRefused: z.int().nonnegative(),
  /** Zero AQUI, com `profilesRead` acima de zero, é o vazio verdadeiro. */
  mediaEligible: z.int().nonnegative(),
  mediaNew: z.int().nonnegative(),
  mediaUpdated: z.int().nonnegative(),
});
export type RadarRunReceiptDto = z.infer<typeof RadarRunReceiptSchema>;

/**
 * O que ESTÁ MONTADO neste ambiente, separado do que foi LIDO.
 *
 * A auditoria de 06/09/2026 mediu, em produção, `DEVMIL_RADAR_PROFILES_PATH`
 * presente e `DEVMIL_RADAR_HORARIOS` ausente. As duas ausências são diferentes,
 * e a segunda explica sozinha por que ninguém roda: sem horário, a tarefa nem
 * entra na lista do agendador.
 *
 * Isto NÃO é disponibilidade. É a resposta a "por que nunca rodou?", e sai ao
 * lado do recibo justamente para a tela nunca mais confundir as duas.
 */
export const RadarSetupSchema = z.strictObject({
  perfisConfigurados: z.boolean(),
  horariosConfigurados: z.boolean(),
  curadoriaConfigurada: z.boolean(),
});
export type RadarSetupDto = z.infer<typeof RadarSetupSchema>;

// ---------------------------------------------------------------------------
// A CURADORIA humana
// ---------------------------------------------------------------------------

/**
 * Sob que direito uma referência curada entra.
 *
 * Lista de PERMISSÃO, e obrigatória: uma referência sem direito declarado é
 * justamente a que ninguém consegue defender depois. Nenhum dos três autoriza
 * copiar mídia ou legenda de terceiro; os três autorizam CITAR e CREDITAR.
 */
export const CuratedRightsSchema = z.enum([
  /** Post público de terceiro: citamos o endereço e creditamos o autor. */
  'referencia_publica_creditada',
  /** O próprio autor autorizou a menção, por escrito, fora deste sistema. */
  'autorizado_pelo_autor',
  /** Publicação institucional ou de veículo: o anúncio, o estudo, a matéria. */
  'fonte_oficial',
]);
export type CuratedRights = z.infer<typeof CuratedRightsSchema>;

/**
 * Uma referência curada por gente, como o arquivo a escreve.
 *
 * Ela é irmã de `ReferenceProfileSchema` e carrega a mesma decisão central: o
 * campo escrito por uma pessoa é OBRIGATÓRIO, e é ele que impede a lista de
 * virar depósito.
 *
 * Não existe campo para legenda alheia, para miniatura nem para métrica.
 * Curadoria não mede nada: ela aponta um endereço, credita quem publicou,
 * declara sob que direito e diz por que aquilo interessa. Fingir número aqui
 * seria a mesma mentira que este lote fecha do outro lado.
 */
export const CuratedReferenceEntrySchema = z.strictObject({
  endereco: z.url().refine((valor) => valor.startsWith('https://'), {
    message: 'o endereço da referência precisa ser https',
  }),
  /**
   * Quem a peça vai creditar, escrito como deve aparecer na legenda.
   *
   * Texto, e não arroba, porque uma referência curada pode ser de um veículo e
   * não de um perfil. Cobrar formato de arroba de um jornal produziria crédito
   * errado, e crédito errado é pior que crédito nenhum.
   */
  creditar: z.string().trim().min(2).max(120),
  formato: ViralMediaTypeSchema,
  /** Quando o conteúdo referenciado foi publicado. */
  publicadoEm: IsoUtcDateTimeSchema,
  motivo: z.string().trim().min(20).max(400),
  direitos: CuratedRightsSchema,
});
export type CuratedReferenceEntry = z.infer<typeof CuratedReferenceEntrySchema>;

export const CuratedReferenceFileSchema = z.strictObject({
  /** JSON não tem comentário, e arquivo de política sem motivo vira lista. */
  sobre: z.string().trim().min(20).max(3_000),
  /** Quem assina esta curadoria. Fica gravado em cada linha. */
  curador: z.string().trim().min(2).max(120),
  /**
   * Pode nascer VAZIA, ao contrário da lista de perfis.
   *
   * A lista de perfis vazia seria um radar sem alvo, que é defeito. A lista de
   * curadoria vazia é o estado inicial legítimo: ninguém curou nada ainda. O
   * arquivo existe desde o primeiro dia para o caminho estar montado quando o
   * dono quiser usá-lo, e inventar uma referência para ele não ficar vazio
   * seria fabricar curadoria que ninguém fez.
   */
  referencias: z.array(CuratedReferenceEntrySchema).max(60),
});
export type CuratedReferenceFile = z.infer<typeof CuratedReferenceFileSchema>;

/** Uma referência curada, como a tela a lê. */
export const CuratedReferenceSchema = z.strictObject({
  id: UuidV4Schema,
  creditTo: z.string().min(2).max(120),
  permalink: z.url(),
  mediaType: ViralMediaTypeSchema,
  publishedAt: IsoUtcDateTimeSchema,
  curatedAt: IsoUtcDateTimeSchema,
  curatedBy: z.string().min(2).max(120),
  note: z.string().min(20).max(400),
  rights: CuratedRightsSchema,
  /** A pauta que já nasceu daqui. Não nula, o botão some, como na descoberta. */
  topicId: UuidV4Schema.nullable(),
});
export type CuratedReferenceDto = z.infer<typeof CuratedReferenceSchema>;

export const CuratedIntakeLineSchema = z.strictObject({
  permalink: z.url(),
  desfecho: z.enum(['nova', 'ja_existia', 'recusada']),
  motivo: z.string().max(160).nullable(),
});
export type CuratedIntakeLineDto = z.infer<typeof CuratedIntakeLineSchema>;

export const CuratedIntakeReportSchema = z.strictObject({
  aplicou: z.boolean(),
  curador: z.string().min(2).max(120),
  novas: z.int().nonnegative(),
  repetidas: z.int().nonnegative(),
  recusadas: z.int().nonnegative(),
  linhas: z.array(CuratedIntakeLineSchema),
});
export type CuratedIntakeReportDto = z.infer<typeof CuratedIntakeReportSchema>;

// ---------------------------------------------------------------------------
// O que o painel lê
// ---------------------------------------------------------------------------

export const ViralScoreBreakdownSchema = z.strictObject({
  engajamento: z.number().min(0).max(1),
  recencia: z.number().min(0).max(1),
  tema: z.number().min(0).max(1),
  formato: z.number().min(0).max(1),
});
export type ViralScoreBreakdownDto = z.infer<typeof ViralScoreBreakdownSchema>;

export const ViralMetricsSchema = z.strictObject({
  likeCount: z.int().nonnegative(),
  commentCount: z.int().nonnegative(),
  viewCount: z.int().nonnegative().nullable(),
  followerCount: z.int().positive(),
  capturedAt: IsoUtcDateTimeSchema,
  /**
   * A variação desde a PRIMEIRA captura desta mídia.
   *
   * Nula quando só existe uma captura, e nula é o certo: zero de variação
   * significa "medi duas vezes e não mudou", que é notícia. "Só medi uma vez"
   * não é notícia nenhuma, e as duas não podem sair iguais na tela.
   */
  deltaLikes: z.int().nullable(),
  deltaComments: z.int().nullable(),
});
export type ViralMetricsDto = z.infer<typeof ViralMetricsSchema>;

export const ViralPostSchema = z.strictObject({
  id: UuidV4Schema,
  profileUsername: InstagramUsernameSchema,
  mediaType: ViralMediaTypeSchema,
  permalink: z.url(),
  /** Expira. A tela cai num quadro neutro, e nada é copiado para o disco. */
  thumbnailUrl: z.url().nullable(),
  caption: z.string().max(MAX_CAPTION_LENGTH).nullable(),
  postedAt: IsoUtcDateTimeSchema,
  score: z.number().min(0).max(1),
  scoreBreakdown: ViralScoreBreakdownSchema,
  themeTerms: z.array(z.string().min(1).max(40)),
  metrics: ViralMetricsSchema,
  reading: ViralReadingSchema.nullable(),
  /**
   * A pauta que já nasceu desta linha. Não nula, o botão "Virar pauta" some e
   * a linha diz que já virou: criar a segunda pauta da mesma tendência é o
   * caminho para publicar duas peças sobre a mesma coisa.
   */
  topicId: UuidV4Schema.nullable(),
  illustrative: z.boolean(),
});
export type ViralPostDto = z.infer<typeof ViralPostSchema>;

/**
 * A página inteira, e ela tem TRÊS estados e não dois.
 *
 * `available` com lista cheia, `available` com lista vazia (o radar rodou e
 * nada passou da régua) e `unavailable` com o motivo. O terceiro é o estado de
 * hoje, enquanto o dono não acender a credencial, e ele NÃO pode se parecer com
 * o segundo: lista vazia diz "hoje não viralizou nada", que seria mentira.
 *
 * ## O que mudou em 06/09/2026, e por quê (achado F14)
 *
 * Os três estados já estavam escritos aqui, e a implementação só conseguia
 * produzir dois: o serviço decidia `available` porque a variável com o caminho
 * da lista estava preenchida, e a tela então dizia "o radar rodou" e "os perfis
 * foram consultados" sem nenhuma rodada ter acontecido.
 *
 * Por isso `lastRun` e `setup` entram no contrato, e entram nos DOIS ramos. O
 * recibo é o que transforma "rodou e não achou" em fato verificável, e a
 * montagem é o que responde "por que nunca rodou" sem que a tela precise
 * adivinhar. Sem os dois, disponibilidade continuaria sendo dedução.
 *
 * `curated` também aparece nos dois ramos, e essa é a decisão que sustenta a
 * operação de hoje: enquanto a descoberta automática estiver indisponível, a
 * curadoria humana é o caminho viável, e ela não pode sumir da tela junto com
 * o motivo da indisponibilidade.
 */
export const ViralRadarSchema = z.discriminatedUnion('availability', [
  z.strictObject({
    availability: z.literal('available'),
    illustrative: z.boolean(),
    localDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
    posts: z.array(ViralPostSchema),
    lastRun: RadarRunReceiptSchema.nullable(),
    setup: RadarSetupSchema,
    curated: z.array(CuratedReferenceSchema),
  }),
  z.strictObject({
    availability: z.literal('unavailable'),
    illustrative: z.boolean(),
    reason: RadarUnavailableReasonSchema,
    graphCode: z.string().max(120).nullable(),
    /** Nulo é "nenhuma rodada foi gravada", que é um dos motivos possíveis. */
    lastRun: RadarRunReceiptSchema.nullable(),
    setup: RadarSetupSchema,
    curated: z.array(CuratedReferenceSchema),
  }),
]);
export type ViralRadarDto = z.infer<typeof ViralRadarSchema>;

export const CreateTrendTopicResultSchema = z.strictObject({
  topicId: UuidV4Schema,
  viralPostId: UuidV4Schema,
});
export type CreateTrendTopicResultDto = z.infer<
  typeof CreateTrendTopicResultSchema
>;
